<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { useConfirm } from 'primevue/useconfirm';
import { useArticleManagement } from 'src/composables/useArticleManagement';
import SharedSplitPane from 'src/components/shared/SharedSplitPane.vue';
import ArticleListTable from 'src/components/article-management/ArticleListTable.vue';
import ArticleDetailPanel from 'src/components/article-management/ArticleDetailPanel.vue';
import ArticleCreateDialog from 'src/components/article-management/ArticleCreateDialog.vue';
import type { FetchArticleResponseLite } from 'src/api/models/fetchArticleResponseLite';
import type { PatchArticleDTO } from 'src/api/models/patchArticleDTO';
import type { CreateArticleDTO } from 'src/api/models/createArticleDTO';
import type { CreateArticleOptionDTO } from 'src/api/models';
import type { PatchArticleOptionDTO } from 'src/api/models/patchArticleOptionDTO';

const {
  articles,
  loading,
  page,
  totalPages,
  totalRecords,
  searchQuery,
  selectedArticleId,
  selectedArticle,
  selectedArticleLoading,
  operationInProgress,
  loadArticles,
  selectArticle,
  updateArticle,
  createArticle,
  setPreferredOption,
  addArticleOption,
  updateArticleOption,
  removeArticleOption,
  searchSuppliers,
} = useArticleManagement();

const confirm = useConfirm();
const detailPanelRef = ref<InstanceType<typeof ArticleDetailPanel> | null>(null);
const splitPaneRef = ref<InstanceType<typeof SharedSplitPane> | null>(null);
const showCreateDialog = ref(false);
const splitPercent = ref(0);

const debouncedSearch = useDebounceFn(() => loadArticles(1), 300);

watch(searchQuery, () => debouncedSearch());

onMounted(() => {
  loadArticles();
});

function openArticle(id: string | null) {
  const isOpening = !selectedArticleId.value;
  selectArticle(id);
  if (isOpening) splitPaneRef.value?.setInitialOpenPercent();
}

function onRowClick (article: FetchArticleResponseLite) {
  const targetId = article.id ?? null;
  if (targetId === selectedArticleId.value) return;
  if (detailPanelRef.value?.isDirty) {
    confirm.require({
      message: 'You have unsaved changes. Discard?',
      header: 'Unsaved changes',
      icon: 'pi pi-exclamation-triangle',
      acceptClass: 'p-button-danger',
      acceptLabel: 'Discard',
      rejectLabel: 'Go back',
      accept: () => openArticle(targetId),
    });
    return;
  }
  openArticle(targetId);
}

function onPageChange (targetPage: number) {
  loadArticles(targetPage);
}

function onUpdateArticle (id: string, data: PatchArticleDTO) {
  updateArticle(id, data);
}


function onSetPreferredOption (articleId: string, optionId: string) {
  setPreferredOption(articleId, optionId);
}

function onAddOption (articleId: string, data: CreateArticleOptionDTO) {
  addArticleOption(articleId, data);
}

function onUpdateOption (optionId: string, data: PatchArticleOptionDTO) {
  updateArticleOption(optionId, data);
}

function onRemoveOption (articleId: string, optionId: string) {
  removeArticleOption(articleId, optionId);
}

async function onCreateArticle (data: CreateArticleDTO) {
  try {
    await createArticle(data);
    showCreateDialog.value = false;
  } catch {
    // keep dialog open on failure — toast shown by composable
  }
}
</script>

<template>
  <div class="flex flex-col h-full">
    <Toolbar class="border-none border-b surface-ground px-4 py-3">
      <template #start>
        <div class="flex items-center gap-3">
          <InputText
            data-testid="article-search"
            v-model="searchQuery"
            placeholder="Search by name, reference, brand..."
            class="w-72"
            size="small"
          />
          <span v-if="totalRecords > 0" class="text-sm text-color-secondary">{{ totalRecords }} articles</span>
        </div>
      </template>
      <template #end>
        <Button
          data-testid="article-create-btn"
          icon="pi pi-plus"
          label="New Article"
          severity="success"
          size="small"
          @click="showCreateDialog = true"
        />
      </template>
    </Toolbar>

    <SharedSplitPane
      ref="splitPaneRef"
      storage-key="article-split-percent"
      :detail-open="!!selectedArticleId"
      class="mt-2"
      @update:split-percent="splitPercent = $event"
    >
      <template #list>
        <ArticleListTable
          :articles="articles"
          :loading="loading"
          :page="page"
          :total-pages="totalPages"
          :total-records="totalRecords"
          :selected-article-id="selectedArticleId"
          :compact="!!selectedArticleId"
          @row-click="onRowClick"
          @page-change="onPageChange"
        />
      </template>
      <template #detail>
        <ArticleDetailPanel
          ref="detailPanelRef"
          :article="selectedArticle"
          :loading="selectedArticleLoading"
          :operation-in-progress="operationInProgress"
          :split-percent="splitPercent"
          :search-supplier-fn="searchSuppliers"
          @update-article="onUpdateArticle"
          @set-preferred-option="onSetPreferredOption"
          @add-option="onAddOption"
          @update-option="onUpdateOption"
          @remove-option="onRemoveOption"
          @close="selectArticle(null)"
        />
      </template>
    </SharedSplitPane>

    <ArticleCreateDialog
      :visible="showCreateDialog"
      :disabled="operationInProgress"
      :search-supplier-fn="searchSuppliers"
      @update:visible="showCreateDialog = $event"
      @submit="onCreateArticle"
    />
  </div>
</template>
